import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { errorStyle } from '../../../constants/styles';
import Button from '../../Button';
import { TextInput } from '../../Inputs';
import { useAuth } from '../AuthContextProvider';
import { AuthValidationError } from '../types';
import RegisterPasswordSidebar from './SignUp/SignupPasswordSidebar';
import { toast } from 'react-toastify';

interface UpdatePasswordProps {
  password: string;
  password_confirmation: string;
}
type InputErrorKey = keyof UpdatePasswordProps;

const UpdatePasswordView: React.FC<any> = () => {
  const { actions } = useAuth();
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm<UpdatePasswordProps>({
    defaultValues: {
      password: '',
      password_confirmation: '',
    },
  });

  const [passwordStrength, setPasswordStrength] = useState({
    characters: false,
    uppercase: false,
    special: false,
    number: false,
    match: false,
  });

  const watchPassword = watch(['password', 'password_confirmation']);

  useEffect(() => {
    setPasswordStrength({
      characters: watchPassword[0].length > 7,
      uppercase: /[A-Z]/.test(watchPassword[0]),
      special: /[\W_]/.test(watchPassword[0]),
      number: /[0-9]/.test(watchPassword[0]),
      match: watchPassword[0] !== '' && watchPassword[0] === watchPassword[1],
    });
  }, [watchPassword[0], watchPassword[1]]); // eslint-disable-line

  const handlePasswordChange = async ({
    password,
    password_confirmation,
  }: UpdatePasswordProps) => {
    setLoading(true);

    try {
      await actions.resetPassword(password, password_confirmation);
      toast('Your password has been updated');
      reset();
    } catch (error) {
      if (error instanceof AuthValidationError) {
        toast(error.message, { type: 'error' });

        const errors = error.errors;
        const keys = Object.keys(errors) as InputErrorKey[];

        keys.forEach(errKey => {
          // @ts-ignore
          setError(errKey, { type: 'api', message: errors[errKey] as string });
        });
      } else {
        console.error(error);
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex justify-between items-center w-full">
      <RegisterPasswordSidebar passwordStrength={passwordStrength} />
      <div className="flex items-center justify-center w-full">
        <div className="max-w-sm w-full">
          <form
            id="auth-update-password"
            onSubmit={handleSubmit(handlePasswordChange)}
          >
            <div className="my-6">
              <Controller
                name="password"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    type="password"
                    placeholder="Password"
                    value={value}
                    onChange={v => onChange(v)}
                    invalid={!!errors.password}
                  />
                )}
              />
            </div>
            <div className="my-6">
              <Controller
                name="password_confirmation"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    type="password"
                    value={value}
                    onChange={v => onChange(v)}
                    invalid={!!errors.password}
                    placeholder="Password Confirmation"
                  />
                )}
              />

              {errors.password && errors.password.type === 'required' && (
                <span className={errorStyle}>Password is required</span>
              )}

              {errors.password && errors.password.type === 'api' && (
                <span className={errorStyle}>{errors.password.message}</span>
              )}
            </div>
            <Button
              type="submit"
              text="Update Password"
              buttonStyle="gradiented"
              loading={loading}
              disabled={loading}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePasswordView;
